"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.ArrangeAnnotations = void 0;
exports.distributeLabelsVertically = distributeLabelsVertically;
exports.sortAnnotationsByYCoord = sortAnnotationsByYCoord;
exports.labelHeight = labelHeight;
exports.moveLabelTowardsParent = moveLabelTowardsParent;
const model_1 = require("../model");
const fetchable_1 = require("../rest/fetchable");
const draw_state_1 = require("./draw.state");
const label_draw_svg_1 = require("./drawable/label.draw.svg");
/**
 * ArrangeAnnotations moves the Label drawables on a given {@link IkeDraw} so that
 * they are neatly arranged without overlapping.
 * @param draw The IkeDraw to arrange labels for
 * @param renderOptions The SVGRenderOptions currently being used to render the IkeDraw
 * @param margin How many pixels to place between adjacent labels
 * @param iterations Optional. The number of times to run the arrangement algorithm (defaults to 100)
 */
class ArrangeAnnotations {
    constructor(draw, renderOptions, margin = 20, iterations = 100) {
        this.draw = draw;
        this.renderOptions = renderOptions;
        this.margin = margin;
        this.iterations = iterations;
    }
    get markersWithLabels() {
        return this.draw.drawState
            .getAllDrawables()
            .filter((d) => model_1.CollectionModelDrawableMarker.isMarker(d) || model_1.CollectionModelDrawableMarkerOffset.isBaseOffset(d))
            .filter((m) => m.label);
    }
    get linesWithLabels() {
        return this.draw.drawState
            .getAllDrawables()
            .filter((d) => model_1.CollectionModelDrawableLine.isLine(d))
            .filter((m) => m.label);
    }
    get verticalSeparations() {
        return this.draw.drawState.getAllDrawables().filter((d) => d instanceof model_1.CollectionModelDrawableVerticalSeparation);
    }
    get labels() {
        return [
            ...this.markersWithLabels.map((m) => m.label),
            ...this.linesWithLabels.map((m) => m.label),
            ...this.verticalSeparations.map((m) => m.label),
        ];
    }
    get drawableWithLabelsOnLeftSide() {
        const leftSideMarkers = this.markersWithLabels.filter((m) => !m.isInsightMeasurement && m.label.isOnLeftSide());
        const insightDetectionsNotWires = this.markersWithLabels.filter((m) => { var _a; return m instanceof model_1.CollectionModelDrawableMarker && m.isInsightMeasurement && ((_a = m.insight) === null || _a === void 0 ? void 0 : _a.detection_class) !== 'wire'; });
        const leftSideVertSep = this.verticalSeparations.filter((d) => d.label.isOnLeftSide());
        return [...leftSideMarkers, ...insightDetectionsNotWires, ...leftSideVertSep];
    }
    get drawablesWithLabelsOnRightSide() {
        const rightSideMarkers = this.markersWithLabels.filter((m) => !m.isInsightMeasurement && m.label.isOnRightSide());
        const insightDetectionsWires = this.markersWithLabels.filter((m) => { var _a; return m instanceof model_1.CollectionModelDrawableMarker && m.isInsightMeasurement && ((_a = m.insight) === null || _a === void 0 ? void 0 : _a.detection_class) === 'wire'; });
        const rightSideVertSep = this.verticalSeparations.filter((d) => d.label.isOnRightSide());
        return [...rightSideMarkers, ...insightDetectionsWires, ...this.linesWithLabels, ...rightSideVertSep];
    }
    run() {
        try {
            for (const leftMarker of this.drawableWithLabelsOnLeftSide) {
                leftMarker.label.snapToLeftSide();
            }
            for (const rightMarker of this.drawablesWithLabelsOnRightSide) {
                rightMarker.label.snapToRightSide();
            }
            distributeLabelsVertically(this.draw, this.renderOptions, this.drawableWithLabelsOnLeftSide);
            distributeLabelsVertically(this.draw, this.renderOptions, this.drawablesWithLabelsOnRightSide);
            for (let i = 0; i < this.iterations; i++) {
                // Alternate between moving labels in regular and reverse order for each pass.
                const labels = i % 2 === 0 ? this.labels : this.labels.reverse();
                for (const label of labels) {
                    const sameSideLabels = label.isOnLeftSide()
                        ? this.drawableWithLabelsOnLeftSide
                        : this.drawablesWithLabelsOnRightSide;
                    const otherLabels = sameSideLabels.map((annotation) => annotation.label).filter((other) => other !== label);
                    if (label.parent) {
                        moveLabelTowardsParent(label, label.parent, otherLabels, this.draw, this.renderOptions, this.margin);
                    }
                }
            }
        }
        catch (error) {
            if (error instanceof fetchable_1.IkeFetchableRaceError) {
                this.draw.log.warn({ error }, 'Caught IkeFetchableRaceError');
                return;
            }
            throw error;
        }
    }
}
exports.ArrangeAnnotations = ArrangeAnnotations;
/**
 * Distribute the given labels of the given markers/lines vertically so that there is
 * an equal amount of space between each label on the Y-axis.
 * The top and bottom labels will touch the top and bottom edge of the image respectively.
 * The labels will be sorted on the Y axis in the same order as their corresponding parents.
 * @param capture The image containing the labels
 * @param annotations The markers/lines whose labels will be distributed vertically
 */
function distributeLabelsVertically(draw, renderOptions, annotations) {
    // Do not attempt to distribute if less than two labels are present
    if (annotations.length < 2) {
        return;
    }
    // Set the Y coord of each label to be equal to their corresponding marker
    for (const annotation of annotations) {
        annotation.label.move(annotation.label.x, annotation.getLabelConnectionPoint(renderOptions).y);
    }
    // Calculate the margin to place between each label
    const sumOfLabelHeights = annotations.reduce((sum, m) => sum + labelHeight(draw, renderOptions, m.label), 0);
    const margin = (draw.capture.resolution.height - sumOfLabelHeights) / (annotations.length - 1);
    // Sort the markers/lines (and their labels) by their Y coordinates,
    // so that we can iterate through them from highest to lowest vertical position
    const sortedAnnotations = sortAnnotationsByYCoord(annotations);
    let offsetFromTop = 0;
    // Distribute the labels with equal margin between them.
    // The labels will now be sorted on the Y axis in the same order as their corresponding markers.
    for (const label of sortedAnnotations.map((m) => m.label)) {
        label.move(label.x, Math.round(offsetFromTop));
        offsetFromTop += labelHeight(draw, renderOptions, label) + margin;
    }
}
function sortAnnotationsByYCoord(annotations) {
    return [...annotations].sort((a, b) => {
        let aY = a.y;
        let bY = b.y;
        if (model_1.CollectionModelDrawableLine.isLine(a)) {
            aY = a.end.getVector().y;
        }
        if (model_1.CollectionModelDrawableLine.isLine(b)) {
            bY = b.end.getVector().y;
        }
        return aY - bY;
    });
}
function labelHeight(draw, renderOptions, label) {
    if (!label.parent) {
        return label_draw_svg_1.LabelSVGDraw.height(1) * draw_state_1.DEFAULT_SCALE;
    }
    const textLines = draw.getLabelText(label.parent.id, renderOptions);
    return label_draw_svg_1.LabelSVGDraw.height(textLines.length) * draw_state_1.DEFAULT_SCALE;
}
/**
 * Move a label towards a given marker/line vertically until it either hits another label,
 * or becomes vertically centered at the Y-coordinate of the marker/line.
 * @param label The label to move
 * @param parent The marker/line to move towards
 */
function moveLabelTowardsParent(label, parent, otherLabels, draw, renderOptions, margin) {
    const parentY = parent.getLabelConnectionPoint(renderOptions).y;
    if (label.y === parentY) {
        return;
    }
    const direction = label.y - parentY > 0 ? 'up' : 'down';
    const closestLabel = direction === 'up'
        ? getClosestLabelUp(label, otherLabels, draw, renderOptions)
        : getClosestLabelDown(label, otherLabels, draw, renderOptions);
    const targetPos = direction === 'up'
        ? getTargetPositionUp(closestLabel, label, parent, margin, draw, renderOptions)
        : getTargetPositionDown(closestLabel, label, parent, margin, draw, renderOptions);
    label.move(label.x, targetPos);
}
function getTargetPositionUp(closestLabel, label, parent, margin, draw, renderOptions) {
    const targetPosY = parent.getLabelConnectionPoint(renderOptions).y;
    if (!closestLabel) {
        return targetPosY;
    }
    const targetClosestLabelHeight = closestLabel.y + labelHeight(draw, renderOptions, closestLabel) + margin;
    if (label.y - targetClosestLabelHeight < label.y - targetPosY) {
        return targetClosestLabelHeight;
    }
    return Math.max(0, targetPosY);
}
function getTargetPositionDown(closestLabel, label, parent, margin, draw, renderOptions) {
    const height = labelHeight(draw, renderOptions, label);
    // If the parent is near the bottom edge of the image, only slide to the bottom edge of the image if that's closer
    const targetPosY = Math.min(draw.capture.resolution.height - labelHeight(draw, renderOptions, label), parent.getLabelConnectionPoint(renderOptions).y);
    if (!closestLabel) {
        return targetPosY;
    }
    const targetClosestLabelHeight = closestLabel.y - height - margin;
    // If the distance between the bottom of the label and the top of the closest label (plus a margin) is
    // less than the distance between the bottom of the label and the parent, then target the top of the closest label (plus a margin).
    if (targetClosestLabelHeight - label.y < targetPosY - label.y) {
        return targetClosestLabelHeight;
    }
    return targetPosY;
}
function getClosestLabelUp(label, otherLabels, draw, renderOptions) {
    let closest = null;
    let closestBottom = null;
    for (const otherLabel of otherLabels) {
        const otherLabelBottom = labelHeight(draw, renderOptions, otherLabel) + otherLabel.y;
        if (otherLabelBottom > label.y) {
            // Other label is overlapping or below, so skip it.
            continue;
        }
        if (!closest || !closestBottom) {
            closest = otherLabel;
            closestBottom = otherLabelBottom;
            continue;
        }
        if (otherLabelBottom < label.y && otherLabelBottom > closestBottom) {
            closest = otherLabel;
            closestBottom = otherLabelBottom;
        }
    }
    return closest;
}
function getClosestLabelDown(label, otherLabels, draw, renderOptions) {
    const bottom = labelHeight(draw, renderOptions, label) + label.y;
    let closest = null;
    for (const otherLabel of otherLabels) {
        if (otherLabel.y < bottom) {
            // Other label is overlapping or below, so skip it.
            continue;
        }
        if (!closest) {
            closest = otherLabel;
            continue;
        }
        if (otherLabel.y > bottom && otherLabel.y < closest.y) {
            closest = otherLabel;
        }
    }
    return closest;
}
//# sourceMappingURL=ArrangeAnnotations.js.map